import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useEffect, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';
import { useGetTaskTemplatesSLAQuery, useUpdateTaskTemplateMutation, useLazyExportTaskSLAsQuery } from '../../../../../frontend/services/tasksSlice/tasksSlice';
import { TaskTemplateSLA, UpdateTaskTemplateBody } from '../../../../../shared/types/Task';
import { showSnackbar } from '../../../../../frontend/utils/snackbar';
import { useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import { useEditDepartmentDisplayOrderIdMutation, useEditDepartmentMutation } from '../../../../../frontend/services/departmentSlice/departmentSlice';

// Rainbow colors for department backgrounds
const RAINBOW_COLORS = [
    '#FF7F00', // Orange
    '#FFD700', // Yellow (darker for better contrast)
    '#00AA00', // Green (darker for better contrast)
    '#1f1fbeff', // Blue
    '#7321adff', // Indigo
    '#913ed4ff', // Violet
    '#cf3367ff', // Pink
    '#00BCD4', // Cyan
    '#795548', // Brown
];


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: typographyTokens.fontSizes.body,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));



const TaskTemplateSLAUpdates: React.FC = () => {
    const [data, setData] = useState<TaskTemplateSLA[]>([]);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editedRow, setEditedRow] = useState<TaskTemplateSLA | null>(null);
    const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [editingDepartmentId, setEditingDepartmentId] = useState<number | null>(null);
    const [editedDepartmentOrder, setEditedDepartmentOrder] = useState<string>('');
    const [isSavingDepartment, setIsSavingDepartment] = useState<boolean>(false);
    const [editDepartmentDisplayOrderId] = useEditDepartmentDisplayOrderIdMutation();
    const { data: getTaskTemplatesSLA, isLoading } = useGetTaskTemplatesSLAQuery();
    const [updateTaskTemplate] = useUpdateTaskTemplateMutation();
    const dispatch = useDispatch();
    const [exportTaskSLAs, { data: exportFileUrl, isSuccess: isExportSuccess }] = useLazyExportTaskSLAsQuery();

    // Get unique departments from the data
    const allDepartments = useMemo(() => {
        if (!getTaskTemplatesSLA) return [];
        const deptMap = new Map<number, string>();
        getTaskTemplatesSLA.forEach((item) => {
            if (!deptMap.has(item.departmentID)) {
                deptMap.set(item.departmentID, item.departmentName);
            }
        });
        return Array.from(deptMap.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.id - b.id);
    }, [getTaskTemplatesSLA]);

    // Initialize selected departments to all when data loads
    useEffect(() => {
        if (allDepartments.length > 0 && selectedDepartments.length === 0) {
            setSelectedDepartments(allDepartments.map((dept) => dept.id));
        }
    }, [allDepartments]);

    useEffect(() => {
        if (getTaskTemplatesSLA) {
            // Filter by selected departments and sort
            const filteredData = getTaskTemplatesSLA.filter((item) =>
                selectedDepartments.includes(item.departmentID)
            );
            // Sort departments by display order, placing unordered departments last.
            // Within each department, sort tasks by display order, then by name.
            const sortedData = [...filteredData].sort((a, b) => {
                if (a.departmentID !== b.departmentID) {
                    const departmentOrderA = a.departmentDisplayOrderId;
                    const departmentOrderB = b.departmentDisplayOrderId;
                    const hasOrderA = departmentOrderA !== null && departmentOrderA !== undefined;
                    const hasOrderB = departmentOrderB !== null && departmentOrderB !== undefined;

                    if (hasOrderA && hasOrderB && departmentOrderA !== departmentOrderB) {
                        return departmentOrderA - departmentOrderB;
                    }
                    if (hasOrderA !== hasOrderB) {
                        return hasOrderA ? -1 : 1;
                    }

                    const departmentNameComparison = a.departmentName.localeCompare(b.departmentName);
                    if (departmentNameComparison !== 0) {
                        return departmentNameComparison;
                    }
                }

                const taskOrderA = a.taskOrderId;
                const taskOrderB = b.taskOrderId;
                const hasTaskOrderA = taskOrderA !== null && taskOrderA !== undefined;
                const hasTaskOrderB = taskOrderB !== null && taskOrderB !== undefined;

                if (hasTaskOrderA && hasTaskOrderB && taskOrderA !== taskOrderB) {
                    return taskOrderA - taskOrderB;
                }
                if (hasTaskOrderA !== hasTaskOrderB) {
                    return hasTaskOrderA ? -1 : 1;
                }

                return a.name.localeCompare(b.name);
            });
            setData(sortedData);
        }
    }, [getTaskTemplatesSLA, selectedDepartments]);

    const handleDepartmentChange = (event: SelectChangeEvent<number[]>) => {
        const value = (event.target as unknown as { value: number[] }).value;
        setSelectedDepartments(value);
    };

    const handleSelectAllDepartments = () => {
        if (isAllSelected) {
            setSelectedDepartments([]);
        } else {
            setSelectedDepartments(allDepartments.map((dept) => dept.id));
        }
    };

    const isAllSelected = selectedDepartments.length === allDepartments.length && allDepartments.length > 0;

    // Calculate department grouping info for merged cells and rainbow colors
    const departmentInfo = useMemo(() => {
        const info: Record<number, { rowSpan: number; colorIndex: number; firstRowId: number }> = {};
        const departmentOrder: number[] = [];

        data.forEach((row) => {
            if (!info[row.departmentID]) {
                info[row.departmentID] = { rowSpan: 1, colorIndex: departmentOrder.length, firstRowId: row.id };
                departmentOrder.push(row.departmentID);
            } else {
                info[row.departmentID].rowSpan += 1;
            }
        });

        return info;
    }, [data]);

    const getDepartmentColor = (departmentID: number) => {
        const colorIndex = departmentInfo[departmentID]?.colorIndex || 0;
        return RAINBOW_COLORS[colorIndex % RAINBOW_COLORS.length];
    };

    const isFirstRowOfDepartment = (row: TaskTemplateSLA) => {
        return departmentInfo[row.departmentID]?.firstRowId === row.id;
    };

    const getDepartmentRowSpan = (departmentID: number) => {
        return departmentInfo[departmentID]?.rowSpan || 1;
    };

    const handleEditClick = (row: TaskTemplateSLA) => {
        setEditingRowId(row.id);
        setEditedRow({ ...row });
    };

    const handleSaveClick = async () => {
        if (editedRow) {
            setIsSaving(true);
            try {
                const taskObject: UpdateTaskTemplateBody = {
                    taskAge: editedRow.requestorSla1 || null,
                    taskOrderId: editedRow.taskOrderId || null,
                    requestorSla: JSON.stringify({ 'requestorSla2': editedRow?.requestorSla2?.toString() || null, 'requestorSla3': editedRow?.requestorSla3?.toString() || null }),
                    stakeholderSla: editedRow.review ? JSON.stringify({ 'stakeholderSla1': editedRow?.stakeholderSla1?.toString() || null, 'stakeholderSla2': editedRow?.stakeholderSla2?.toString() || null, 'stakeholderSla3': editedRow?.stakeholderSla3?.toString() || null }) : null,
                };
                await updateTaskTemplate({ id: editedRow.id, body: taskObject }).unwrap();
                setData((prevData) =>
                    prevData.map((row) => (row.id === editedRow.id ? editedRow : row))
                );
                showSnackbar(dispatch, { successMessage: 'SLA updated successfully' }, 'success', 5000);
            } catch (error) {
                showSnackbar(dispatch, { errorMessage: 'Failed to update SLA. Please try again.' }, 'error', 7000);
            } finally {
                setIsSaving(false);
                setEditingRowId(null);
                setEditedRow(null);
            }
        }
    };

    const handleCancelClick = () => {
        setEditingRowId(null);
        setEditedRow(null);
    };

    const handleEditDepartment = (row: TaskTemplateSLA) => {
        setEditingDepartmentId(row.departmentID);
        setEditedDepartmentOrder(row.departmentDisplayOrderId?.toString() || '');
    };

    const handleSaveDepartment = async (departmentID: number) => {
        const value = editedDepartmentOrder.trim();
        const departmentDisplayOrderId = value === '' ? null : Number(value);

        if (
            departmentDisplayOrderId !== null &&
            (!Number.isSafeInteger(departmentDisplayOrderId) || departmentDisplayOrderId < 1)
        ) {
            showSnackbar(dispatch, { errorMessage: 'Department order must be a positive whole number.' }, 'error', 7000);
            return;
        }

        setIsSavingDepartment(true);
        try {
            await editDepartmentDisplayOrderId({
                id: departmentID,
                data: { departmentDisplayOrderId }
            }).unwrap();

            setData((prevData) => prevData.map((row) => (
                row.departmentID === departmentID
                    ? { ...row, departmentDisplayOrderId }
                    : row
            )));
            setEditingDepartmentId(null);
            setEditedDepartmentOrder('');
            showSnackbar(dispatch, { successMessage: 'Department order updated successfully' }, 'success', 5000);
        } catch (error) {
            showSnackbar(dispatch, { errorMessage: 'Failed to update department order. Please try again.' }, 'error', 7000);
        } finally {
            setIsSavingDepartment(false);
        }
    };

    const handleCancelDepartmentEdit = () => {
        setEditingDepartmentId(null);
        setEditedDepartmentOrder('');
    };

    const handleFieldChange = (
        field: keyof TaskTemplateSLA,
        value: string | number
    ) => {
        if (editedRow) {
            setEditedRow({
                ...editedRow,
                [field]: value,
            });
        }
    };

    const handleNumericChange = (
        field: keyof TaskTemplateSLA,
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = (e.target as unknown as { value: string }).value;
        // Only allow empty string or natural numbers (positive integers)
        if (value === '' || /^[1-9][0-9]*$/.test(value)) {
            const numericValue = value === '' ? 0 : parseInt(value, 10);
            handleFieldChange(field, numericValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow: digits, backspace, delete, tab, escape, enter, arrows
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (allowedKeys.includes(e.key) || /^[0-9]$/.test(e.key)) {
            return;
        }
        e.preventDefault();
    };

    const isEditing = (id: number) => editingRowId === id;

    // Initialize local data when departmentLookups changes
    useEffect(() => {
        if (isExportSuccess && exportFileUrl) {
            const link = document.createElement('a');
            link.href = exportFileUrl;
            link.setAttribute('download', 'task-template-sla-list.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [isExportSuccess, exportFileUrl]);

    const handleExportToExcel = () => {
        exportTaskSLAs();
    }

    return (
        <Box sx={{ padding: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <Typography variant="h5">
                    SLA ( Template Task ) Quick Updates
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<i className="fa-solid fa-file-excel"></i>}
                    sx={{
                        ml: 'auto',
                        mr: 2,
                        backgroundColor: '#217346',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: '#1a5c37',
                        }
                    }}
                    onClick={handleExportToExcel}
                >
                    Export to Excel
                </Button>
                <FormControl sx={{ width: '20vw' }} size="small">
                    <InputLabel id="department-filter-label">Department Filter</InputLabel>
                    <Select
                        labelId="department-filter-label"
                        id="department-filter"
                        multiple
                        value={selectedDepartments}
                        onChange={handleDepartmentChange}
                        input={<OutlinedInput label="Department Filter" />}
                        renderValue={(selected) => {
                            if (selected.length === allDepartments.length) {
                                return 'All Departments';
                            }
                            if (selected.length === 0) {
                                return 'None Selected';
                            }
                            return allDepartments
                                .filter((dept) => selected.includes(dept.id))
                                .map((dept) => dept.name)
                                .join(', ');
                        }}
                    >
                        <MenuItem onClick={handleSelectAllDepartments}>
                            <Checkbox checked={isAllSelected} indeterminate={selectedDepartments.length > 0 && !isAllSelected} />
                            <ListItemText primary="Select All" />
                        </MenuItem>
                        {allDepartments.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                                <Checkbox checked={selectedDepartments.includes(dept.id)} />
                                <ListItemText primary={dept.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                <Table sx={{ minWidth: 700, tableLayout: 'fixed' }} stickyHeader aria-label="task template SLA table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ width: '10%' }}>Department</StyledTableCell>
                            <StyledTableCell sx={{ width: '33%' }}>Task Name</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Task Order</StyledTableCell>
                            <StyledTableCell sx={{ width: '8%' }}>Review Type</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Requestor SLA-1</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Requestor SLA-2</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Requestor SLA-3</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Stakeholder SLA-1</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Stakeholder SLA-2</StyledTableCell>
                            <StyledTableCell sx={{ width: '6%' }}>Stakeholder SLA-3</StyledTableCell>
                            <StyledTableCell sx={{ width: '7%', minWidth: 100 }}>Edit</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <StyledTableCell colSpan={11} sx={{ textAlign: 'center', padding: 4 }}>
                                    <CircularProgress size={40} />
                                    <Typography variant="body1" color="textSecondary" sx={{ marginTop: 2 }}>
                                        Loading data...
                                    </Typography>
                                </StyledTableCell>
                            </TableRow>
                        ) : data.length === 0 ? (
                            <TableRow>
                                <StyledTableCell colSpan={11} sx={{ textAlign: 'center', padding: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        { allDepartments.length === 0 ? 'No data available' : 'Please select at least one department' }
                                    </Typography>
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => (
                                <StyledTableRow key={row.id}>
                                    {isFirstRowOfDepartment(row) && (
                                        <StyledTableCell
                                            component="th"
                                            scope="row"
                                            rowSpan={getDepartmentRowSpan(row.departmentID)}
                                            sx={{
                                                backgroundColor: getDepartmentColor(row.departmentID),
                                                fontWeight: typographyTokens.fontWeights.bold,
                                                textAlign: 'center',
                                                verticalAlign: 'middle',
                                                padding: '8px',
                                                color: '#FFFFFF',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                <Typography component="span" sx={{ fontWeight: typographyTokens.fontWeights.bold }}>
                                                    {row.departmentName}
                                                </Typography>
                                                {editingDepartmentId === row.departmentID ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                        <TextField
                                                            value={editedDepartmentOrder}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                if (value === '' || /^[1-9][0-9]*$/.test(value)) {
                                                                    setEditedDepartmentOrder(value);
                                                                }
                                                            }}
                                                            onKeyDown={handleKeyDown}
                                                            size="small"
                                                            inputProps={{ 'aria-label': 'Department order' }}
                                                            sx={{ width: 70, backgroundColor: '#FFFFFF' }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleSaveDepartment(row.departmentID)}
                                                            disabled={isSavingDepartment}
                                                            title="Save department order"
                                                        >
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={handleCancelDepartmentEdit}
                                                            disabled={isSavingDepartment}
                                                            title="Cancel department order edit"
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                        <Typography component="span" variant="body2">
                                                            {row.departmentDisplayOrderId ?? ''}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditDepartment(row)}
                                                            title="Edit department order"
                                                            sx={{ color: '#FFFFFF', padding: 0.25 }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                        {row.name}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {isEditing(row.id) ? (
                                            <TextField
                                                value={editedRow?.taskOrderId || ''}
                                                onChange={(e) => handleNumericChange('taskOrderId', e)}
                                                onKeyDown={handleKeyDown}
                                                size="small"
                                                sx={{ width: 80 }}
                                            />
                                        ) : (
                                            row.taskOrderId ?? ''
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {row.review ? 'Yes' : 'No'}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {isEditing(row.id) ? (
                                            <TextField
                                                value={editedRow?.requestorSla1 || ''}
                                                onChange={(e) => handleNumericChange('requestorSla1', e)}
                                                onKeyDown={handleKeyDown}
                                                size="small"
                                                sx={{ width: 80 }}
                                            />
                                        ) : (
                                            row.requestorSla1
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {isEditing(row.id) ? (
                                            <TextField
                                                value={editedRow?.requestorSla2 || ''}
                                                onChange={(e) => handleNumericChange('requestorSla2', e)}
                                                onKeyDown={handleKeyDown}
                                                size="small"
                                                sx={{ width: 80 }}
                                            />
                                        ) : (
                                            row.requestorSla2
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {isEditing(row.id) ? (
                                            <TextField
                                                value={editedRow?.requestorSla3 || ''}
                                                onChange={(e) => handleNumericChange('requestorSla3', e)}
                                                onKeyDown={handleKeyDown}
                                                size="small"
                                                sx={{ width: 80 }}
                                            />
                                        ) : (
                                            row.requestorSla3
                                        )}
                                    </StyledTableCell>
                                    {/* Stakeholder SLA columns - only show values when review is 'Yes' */}
                                    <StyledTableCell>
                                        {row.review || (isEditing(row.id) && editedRow?.review) ? (
                                            isEditing(row.id) ? (
                                                <TextField
                                                    value={editedRow?.stakeholderSla1 || ''}
                                                    onChange={(e) => handleNumericChange('stakeholderSla1', e)}
                                                    onKeyDown={handleKeyDown}
                                                    size="small"
                                                    sx={{ width: 80 }}
                                                />
                                            ) : (
                                                row.stakeholderSla1
                                            )
                                        ) : (
                                            ''
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {row.review || (isEditing(row.id) && editedRow?.review) ? (
                                            isEditing(row.id) ? (
                                                <TextField
                                                    value={editedRow?.stakeholderSla2 || ''}
                                                    onChange={(e) => handleNumericChange('stakeholderSla2', e)}
                                                    onKeyDown={handleKeyDown}
                                                    size="small"
                                                    sx={{ width: 80 }}
                                                />
                                            ) : (
                                                row.stakeholderSla2
                                            )
                                        ) : (
                                            ''
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {row.review || (isEditing(row.id) && editedRow?.review) ? (
                                            isEditing(row.id) ? (
                                                <TextField
                                                    value={editedRow?.stakeholderSla3 || ''}
                                                    onChange={(e) => handleNumericChange('stakeholderSla3', e)}
                                                    onKeyDown={handleKeyDown}
                                                    size="small"
                                                    sx={{ width: 80 }}
                                                />
                                            ) : (
                                                row.stakeholderSla3
                                            )
                                        ) : (
                                            ''
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ width: '12%', minWidth: 100 }}>
                                        {isEditing(row.id) ? (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={handleSaveClick}
                                                    disabled={isSaving}
                                                    title="Save"
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={handleCancelClick}
                                                    disabled={isSaving}
                                                    title="Cancel"
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditClick(row)}
                                                title="Edit"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TaskTemplateSLAUpdates;
